import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Clipboard, Upload, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// AI screenshot uploader for the property dialog.
// - Handles paste / file pick / server call
// - On success bubbles the extracted fields + gallery back to the parent
//   via `onExtracted({ extracted, gallery })` so the parent can merge them
//   into its form state.
const ScreenshotUploader = ({ onExtracted }) => {
  const [screenshot, setScreenshot] = useState(null); // { dataUrl, size }
  const [analyzing, setAnalyzing] = useState(false);
  const fileInputRef = useRef(null);

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  // Downscale + re-encode before sending. Cloudflare (in front of the
  // preview URL) rejects POST bodies > ~5MB with HTTP 403 error 1010 —
  // turning a 4K screenshot into a 1600px JPEG keeps us well under.
  const MAX_SIDE = 1600;
  const OUT_QUALITY = 0.82;
  const shrinkDataUrl = (dataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { width: w, height: h } = img;
      const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", OUT_QUALITY));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

  const onFilePicked = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      toast.error("Formato non supportato. Usa PNG, JPEG o WEBP.");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 6 MB)");
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setScreenshot({ dataUrl, size: file.size });
    } catch { toast.error("Impossibile leggere l'immagine"); }
  };

  const onPasteFromClipboard = async () => {
    if (!navigator.clipboard || !navigator.clipboard.read) {
      toast.error("Il tuo browser non supporta l'incolla da clipboard. Usa il pulsante 'Carica immagine'.");
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const type = item.types.find((t) => /^image\//.test(t));
        if (!type) continue;
        const blob = await item.getType(type);
        return onFilePicked(new File([blob], "clipboard.png", { type: blob.type }));
      }
      toast.error("Nessuna immagine trovata negli appunti");
    } catch (err) {
      toast.error("Permesso negato per gli appunti");
    }
  };

  const analyzeScreenshot = async () => {
    if (!screenshot?.dataUrl) return;
    setAnalyzing(true);
    const toastId = toast.loading("Analisi screenshot in corso — l'AI sta estraendo i dati…");
    try {
      // Shrink before upload — big base64 payloads (>5MB) are refused by
      // the Cloudflare edge in front of the preview URL (Error 1010).
      const payload = await shrinkDataUrl(screenshot.dataUrl);
      const r = await api.post("/properties/parse-screenshot", { image_base64: payload });
      const ext = r.data?.extracted || {};
      const gal = r.data?.gallery || [];
      onExtracted?.({ extracted: ext, gallery: gal });
      toast.success(`Campi compilati${gal.length ? ` · ${gal.length} foto aggiunte in galleria` : ""}`, { id: toastId });
      setScreenshot(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const msg = detail
        || (status === 413 ? "Immagine troppo grande per il proxy — prova a ridurla o a fare uno screenshot più piccolo"
        :  status === 403 ? "Richiesta bloccata dal proxy (immagine troppo grande?). Prova con uno screenshot più piccolo."
        :  status === 504 || status === 502 ? "Timeout dall'AI — riprova"
        :  "Errore durante l'analisi dello screenshot");
      toast.error(msg, { id: toastId });
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="ios-card border border-dashed border-[#007AFF]/40 bg-[#007AFF]/5 p-3 mb-3" data-testid="pd-screenshot-panel">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#007AFF]" strokeWidth={1.5} />
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#007AFF]">Compila con AI da screenshot</span>
      </div>
      {!screenshot && (
        <>
          <p className="text-[11px] text-slate-600 mb-2 leading-relaxed">
            Incolla o carica lo screenshot di un annuncio. L'AI estrarrà i campi (indirizzo, prezzo, mq, ecc.) e ritaglierà le foto dell'immobile in una galleria.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onPasteFromClipboard}
              disabled={analyzing}
              data-testid="pd-screenshot-paste"
              className="rounded-full h-9 border-[#007AFF]/40 bg-white"
            >
              <Clipboard className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Incolla screenshot
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={analyzing}
              data-testid="pd-screenshot-upload"
              className="rounded-full h-9 border-[#007AFF]/40 bg-white"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Carica immagine
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => onFilePicked(e.target.files?.[0])}
              data-testid="pd-screenshot-input"
            />
          </div>
        </>
      )}
      {screenshot && (
        <div className="flex items-start gap-3">
          <img
            src={screenshot.dataUrl}
            alt="Screenshot"
            className="w-24 h-24 rounded-xl object-cover border border-slate-200 bg-white"
            data-testid="pd-screenshot-preview"
          />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="text-[11px] text-slate-600">Screenshot pronto. Analizza per riempire i campi qui sotto.</div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={analyzeScreenshot}
                disabled={analyzing}
                data-testid="pd-screenshot-analyze"
                className="rounded-full h-9 bg-[#007AFF] hover:bg-[#0060df] text-white"
              >
                {analyzing
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analizzo…</>
                  : <><Sparkles className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Estrai con AI</>}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setScreenshot(null)}
                disabled={analyzing}
                data-testid="pd-screenshot-clear"
                className="rounded-full h-9 text-slate-500"
              >
                <X className="w-3.5 h-3.5 mr-1.5" /> Rimuovi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenshotUploader;
